import { createSlice, PayloadAction } from "@reduxjs/toolkit"

const initialState = {
  status: "idle",
  startTime: null,
  endTime: null,
  totalPausedMs: 0,
  currentPauseStart: null,
} as TimerState

export const hourReportTimerSlice = createSlice({
  name: "hour-report-timer",
  initialState,
  reducers: {
    start: (_, action: PayloadAction<number | undefined>) => startTimer(action.payload),
    pause: (state) => pauseTimer(state),
    resume: (state) => resumeTimer(state),
    stop: (state) => stopTimer(state),
    updateStart: (state, action: PayloadAction<number>) => updateTimerStart(state, action.payload),
    updateEnd: (state, action: PayloadAction<number>) => updateTimerEnd(state, action.payload),
    reset: () => initialState,
    hydrate: (_, action: PayloadAction<TimerState>) => action.payload,
  },
})

const startTimer = (startTime = Date.now()): RunningTimer => {
  const now = Date.now()
  const timerStartTime = Math.max(now, startTime)
  return {
    status: "running",
    startTime: timerStartTime,
    endTime: null,
    totalPausedMs: 0,
    currentPauseStart: null,
  }
}

const pauseTimer = (timer: TimerState): TimerState => {
  if (!isRunningTimer(timer)) return timer
  const now = Date.now()
  return {
    status: "paused",
    startTime: timer.startTime,
    endTime: null,
    totalPausedMs: timer.totalPausedMs,
    currentPauseStart: now,
  }
}

const resumeTimer = (timer: TimerState): TimerState => {
  if (!isPausedTimer(timer)) return timer
  const now = Date.now()
  const currentPausedMs = now - timer.currentPauseStart
  const totalPausedMs = timer.totalPausedMs + currentPausedMs
  return {
    status: "running",
    startTime: timer.startTime,
    endTime: null,
    totalPausedMs: totalPausedMs,
    currentPauseStart: null,
  }
}

const stopTimer = (timer: TimerState): TimerState => {
  if (!isRunningTimer(timer) && !isPausedTimer(timer)) return timer
  const now = Date.now()
  const currentPausedMs = timer.currentPauseStart ? now - timer.currentPauseStart : 0
  const totalPausedMs = Math.max(timer.totalPausedMs + currentPausedMs, 0)

  return {
    status: "stopped",
    startTime: timer.startTime,
    endTime: now,
    totalPausedMs,
    currentPauseStart: null,
  }
}

const updateTimerStart = (timer: TimerState, startTime: number): TimerState => {
  if (isIdleTimer(timer)) return timer

  const nextStartTime = Math.min(Date.now(), startTime)
  if (isPausedTimer(timer)) {
    const nextPauseStart = Math.max(timer.currentPauseStart, nextStartTime)
    return {
      ...timer,
      startTime: nextStartTime,
      totalPausedMs: 0,
      currentPauseStart: nextPauseStart,
    }
  }

  if (isStoppedTimer(timer)) {
    const nextEndTime = Math.max(timer.endTime, nextStartTime)
    return {
      ...timer,
      startTime: nextStartTime,
      endTime: nextEndTime,
      totalPausedMs: 0,
      currentPauseStart: null,
    }
  }

  return {
    ...timer,
    startTime: nextStartTime,
    totalPausedMs: 0,
  }
}

const updateTimerEnd = (timer: TimerState, endTime: number): TimerState => {
  if (!isStoppedTimer(timer)) return timer
  const nextEndTime = Math.max(timer.startTime, endTime)

  return {
    ...timer,
    endTime: nextEndTime,
    totalPausedMs: 0,
    currentPauseStart: null,
  }
}

export const getElapsedMs = (timer: TimerState): number => {
  if (isIdleTimer(timer)) return 0

  const now = Date.now()

  switch (timer.status) {
    case "running":
      return now - timer.startTime - timer.totalPausedMs

    case "paused":
      return timer.currentPauseStart - timer.startTime - timer.totalPausedMs

    case "stopped":
      return timer.endTime - timer.startTime - timer.totalPausedMs
  }
}

export const getElapsedHours = (timer: TimerState): number => {
  const elapsedMs = getElapsedMs(timer)
  return Math.round((elapsedMs / 3600000) * 100) / 100 // Convert ms to hours and round to 2 decimal places
}

export const isIdleTimer = (timer: TimerState): timer is IdleTimer => timer.status === "idle"
export const isRunningTimer = (timer: TimerState): timer is RunningTimer => timer.status === "running"
export const isPausedTimer = (timer: TimerState): timer is PausedTimer => timer.status === "paused"
export const isStoppedTimer = (timer: TimerState): timer is StoppedTimer => timer.status === "stopped"

export type TimerStatus = "idle" | "running" | "paused" | "stopped"
export type TimerState = IdleTimer | RunningTimer | PausedTimer | StoppedTimer
export default hourReportTimerSlice.reducer
export const { start, pause, resume, stop, updateStart, updateEnd, reset, hydrate } = hourReportTimerSlice.actions

type IdleTimer = {
  status: "idle"
  startTime: null
  endTime: null
  totalPausedMs: 0
  currentPauseStart: null
}
type RunningTimer = {
  status: "running"
  startTime: number
  endTime: null
  totalPausedMs: number
  currentPauseStart: null
}
type PausedTimer = {
  status: "paused"
  startTime: number
  endTime: null
  totalPausedMs: number
  currentPauseStart: number
}
type StoppedTimer = {
  status: "stopped"
  startTime: number
  endTime: number
  totalPausedMs: number
  currentPauseStart: null
}
